import { useEffect, useRef, useState, type ReactNode } from 'react'

/** 주간 막대그래프(SVG) — 판매분석 탭 전용.
 *  표기 규칙(dataviz): 막대 ≤24px·윗모서리 4px 라운드·바닥 기준, 묶음 막대 사이 2px, 격자 1px 실선(옅게),
 *  기준선(기간 평균)만 점선+라벨, 선 2px·표식 r4 + 2px 바탕색 테, 값 라벨은 강조 막대만, 범례는 계열 2개 이상일 때,
 *  막대 칸 전체가 마우스 영역(툴팁). 글자는 계열색을 쓰지 않는다. */

export interface BarSeries { key: string; name: string; color: string; values: number[] }

export const VIZ = {
  surface: '#ffffff',
  grid: '#e7e6e1',
  axis: '#c9c8c2',
  text: '#0b0b0b',
  text2: '#52514e',
  muted: '#8a8984',
  neutral: '#c4c3b9',      // 강조 아닌 막대
  accent: '#2a78d6',       // 강조(최대 판매 주)
  cat: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],   // 품목 순서 고정(검증된 5색)
  overlay: '#eb6834',      // 전년 동기 선
}

/** 눈금 간격 = 1·2·2.5·5 × 10^n 중 (최댓값/4) 이상 최소 → 0, step, 2step… 깔끔한 값만. */
function niceScale(v: number): { max: number; ticks: number[] } {
  if (v <= 0) return { max: 1, ticks: [0, 1] }
  const raw = v / 4
  const p = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * p).find((s) => s >= raw) ?? 10 * p
  const max = Math.ceil(v / step) * step
  return { max, ticks: Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step) }
}

const fmt = (n: number) => n.toLocaleString('ko-KR', { maximumFractionDigits: n < 10 ? 1 : 0 })

function topRoundedBar(x: number, y: number, w: number, h: number, r = 4): string {
  if (h <= 0) return ''
  const rr = Math.min(r, w / 2, h)
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`
}

export function WeeklyBars({
  labels, series, highlight, refLine, overlay, errors, height = 240, tooltip, ariaLabel,
}: {
  labels: string[]
  series: BarSeries[]
  highlight?: number | null                    // 단일 계열일 때 강조할 막대 index
  refLine?: { value: number; label: string } | null
  overlay?: { name: string; values: number[] } | null
  errors?: ({ lo: number; hi: number } | null)[]
  height?: number
  tooltip: (i: number) => ReactNode
  ariaLabel: string
}) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(640)
  const [hover, setHover] = useState<number | null>(null)
  useEffect(() => {
    const el = box.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(320, e.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const n = labels.length
  const m = { top: 22, right: 12, bottom: 26, left: 56 }
  const pw = width - m.left - m.right
  const ph = height - m.top - m.bottom
  const all = [
    ...series.flatMap((s) => s.values),
    ...(errors ?? []).map((e) => e?.hi ?? 0),
    ...(overlay?.values ?? []),
    refLine?.value ?? 0,
  ]
  const { max: ymax, ticks } = niceScale(Math.max(0, ...all) * 1.08)
  const y = (v: number) => m.top + ph - (v / ymax) * ph
  const band = pw / Math.max(1, n)
  const k = series.length
  const barW = Math.max(4, Math.min(24, (band * 0.7 - (k - 1) * 2) / k))
  const groupW = k * barW + (k - 1) * 2
  const single = k === 1
  const showLegend = k > 1 || !!overlay
  const labelEvery = n > 10 && width < 700 ? 2 : 1

  return (
    <div ref={box} className="relative w-full">
      {showLegend && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-1 text-xs" style={{ color: VIZ.text2 }}>
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.color }}/>{s.name}
            </span>
          ))}
          {overlay && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-0.5" style={{ background: VIZ.overlay }}/>{overlay.name}
            </span>
          )}
        </div>
      )}
      <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block"
           onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.left} x2={width - m.right} y1={y(t)} y2={y(t)} stroke={t === 0 ? VIZ.axis : VIZ.grid} strokeWidth={1}/>
            <text x={m.left - 6} y={y(t)} dy="0.32em" textAnchor="end" fontSize={11} fill={VIZ.text2}
                  style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(t)}</text>
          </g>
        ))}
        {hover !== null && (
          <rect x={m.left + hover * band} y={m.top} width={band} height={ph} fill="#0b0b0b" opacity={0.04}/>
        )}
        {labels.map((lb, i) => {
          const gx = m.left + i * band + (band - groupW) / 2
          return (
            <g key={i}>
              {series.map((s, si) => {
                const v = s.values[i] ?? 0
                const color = single ? (highlight === i ? VIZ.accent : VIZ.neutral) : s.color
                return <path key={s.key} d={topRoundedBar(gx + si * (barW + 2), y(v), barW, y(0) - y(v))} fill={color}/>
              })}
              {single && errors?.[i] && (() => {
                const e = errors[i]!
                const cx = gx + barW / 2
                return (
                  <g stroke={VIZ.text} strokeWidth={1.25}>
                    <line x1={cx} x2={cx} y1={y(e.hi)} y2={y(Math.max(0, e.lo))}/>
                    <line x1={cx - 5} x2={cx + 5} y1={y(e.hi)} y2={y(e.hi)}/>
                    <line x1={cx - 5} x2={cx + 5} y1={y(Math.max(0, e.lo))} y2={y(Math.max(0, e.lo))}/>
                  </g>
                )
              })()}
              {single && highlight === i && (
                <text x={gx + barW / 2} y={y(errors?.[i]?.hi ?? series[0].values[i]) - 6} textAnchor="middle"
                      fontSize={11} fontWeight={600} fill={VIZ.text}>{fmt(series[0].values[i])}</text>
              )}
              {i % labelEvery === 0 && (
                <text x={m.left + i * band + band / 2} y={height - 8} textAnchor="middle" fontSize={11} fill={VIZ.text2}>{lb}</text>
              )}
            </g>
          )
        })}
        {refLine && refLine.value > 0 && (
          <g>
            <line x1={m.left} x2={width - m.right} y1={y(refLine.value)} y2={y(refLine.value)}
                  stroke={VIZ.muted} strokeWidth={1} strokeDasharray="4 4"/>
            <text x={width - m.right} y={y(refLine.value) - 5} textAnchor="end" fontSize={11} fill={VIZ.text2}>{refLine.label}</text>
          </g>
        )}
        {overlay && (
          <g>
            <polyline fill="none" stroke={VIZ.overlay} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
                      points={overlay.values.map((v, i) => `${m.left + i * band + band / 2},${y(v)}`).join(' ')}/>
            {overlay.values.map((v, i) => (
              <circle key={i} cx={m.left + i * band + band / 2} cy={y(v)} r={4} fill={VIZ.overlay}
                      stroke={VIZ.surface} strokeWidth={2}/>
            ))}
          </g>
        )}
        {labels.map((_, i) => (
          <rect key={i} x={m.left + i * band} y={m.top} width={band} height={ph} fill="transparent"
                onMouseEnter={() => setHover(i)}/>
        ))}
      </svg>
      {hover !== null && (
        <div className="pointer-events-none absolute z-10 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg"
             style={{ left: Math.min(width - 200, Math.max(0, m.left + hover * band + band / 2 - 90)), top: showLegend ? 28 : 4, minWidth: 180 }}>
          {tooltip(hover)}
        </div>
      )}
    </div>
  )
}
